import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { Lock, Mail, ArrowRight, Leaf, ShieldCheck, AlertCircle } from 'lucide-react';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await login(email, password);
      
      Swal.fire({
        icon: 'success',
        title: 'Access Granted',
        text: 'Welcome back to the Admin Panel',
        timer: 1500,
        showConfirmButton: false,
        background: '#ffffff',
        iconColor: '#2E7D32',
        customClass: {
          title: 'font-poppins font-bold text-sugarcane-charcoal text-lg',
          popup: 'rounded-2xl shadow-xl border border-gray-100'
        }
      });
      
      navigate('/admin');
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Authentication Failed',
        text: 'Invalid email or password. Please try again.',
        confirmButtonColor: '#2E7D32',
        background: '#ffffff',
        customClass: {
          title: 'font-poppins font-bold text-sugarcane-charcoal text-lg',
          popup: 'rounded-2xl shadow-xl border border-gray-100',
          confirmButton: 'rounded-xl px-6 py-2.5 font-semibold text-sm'
        }
      });
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sugarcane-charcoal flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background Decor */}
      <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-sugarcane-green rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-sugarcane-light rounded-full blur-[120px]"></div>
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-4xl bg-white/5 backdrop-blur-3xl rounded-2xl border border-white/10 shadow-2xl flex flex-col lg:flex-row overflow-hidden"
      >
        {/* Left Side: Branding/Visuals */}
        <div className="lg:w-1/2 bg-gradient-to-br from-sugarcane-green to-sugarcane-charcoal p-10 lg:p-14 flex flex-col justify-between relative overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(#ffffff_1px,transparent_1px)] [background-size:30px_30px]"></div>
          </div>
          
          <div className="relative z-10">
            <div className="flex items-center space-x-3 mb-10">
              <div className="bg-white p-2 rounded-xl shadow-lg">
                <Leaf className="text-sugarcane-green w-7 h-7" />
              </div>
              <span className="text-2xl font-montserrat font-bold text-white tracking-tight">
                SUGAR<span className="text-sugarcane-light">CANE</span>
              </span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-6">
              Secured <br />
              <span className="text-sugarcane-light">Management</span> <br />
              Portal
            </h2>
            <p className="text-white/70 text-sm leading-relaxed max-w-xs">
              Access the industrial core of our operations. Only authorized personnel can manage the factory's digital ecosystem.
            </p>
          </div>

          <div className="relative z-10 mt-10">
            <div className="flex items-center space-x-4 text-white/50 text-[10px] font-black uppercase tracking-widest">
              <ShieldCheck className="text-sugarcane-light" size={18} />
              <span>Enterprise Grade Security</span>
            </div>
          </div>
        </div>

        {/* Right Side: Login Form */}
        <div className="lg:w-1/2 bg-white p-10 lg:p-14 flex flex-col justify-center">
          <div className="mb-8">
            <h3 className="text-2xl font-black text-sugarcane-charcoal mb-1">Welcome Back</h3>
            <p className="text-xs text-gray-400 font-medium italic">Please log in to your administrator account.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold tracking-wider text-gray-500 ml-4">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  type="email" 
                  required 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@sugarcane.com"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 pl-12 text-sm font-medium text-sugarcane-charcoal focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all duration-200 outline-none"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold tracking-wider text-gray-500 ml-4">Password</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input 
                  type="password" 
                  required 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 pl-12 text-sm font-medium text-sugarcane-charcoal focus:bg-white focus:border-sugarcane-green focus:ring-2 focus:ring-sugarcane-green/10 transition-all duration-200 outline-none"
                />
              </div>
            </div>

            <div className="pt-2">
              <button 
                type="submit"
                disabled={loading}
                className={`w-full bg-sugarcane-charcoal hover:bg-sugarcane-green text-white py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center space-x-2 transition-all shadow-md shadow-sugarcane-green/10 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                <span>{loading ? 'Authenticating...' : 'Sign In to Dashboard'}</span>
                {!loading && <ArrowRight size={18} />}
              </button>
            </div>
          </form>

          <div className="mt-12 pt-8 border-t border-gray-50 text-center">
            <p className="text-gray-400 text-xs font-medium">
              Forgot password? Contact system administrator.
            </p>
          </div>
        </div>
      </motion.div>

      {/* Decorative Icon Effect */}
      <motion.div 
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="absolute -bottom-20 -left-20 text-white/5 opacity-5 lg:opacity-20"
      >
        <Leaf size={400} />
      </motion.div>
    </div>
  );
};

export default AdminLogin;
